import { useEffect, useRef, useState } from "react";
import type { BillingStatus } from "@io-spoke/contracts/billing";
import { getActiveOrgId } from "../lib/authStorage";
import { confirmSubscriptionCheckout, createCustomerPortal, createSubscriptionCheckout, getBillingStatus } from "../lib/billing";
import { useAsyncCallback } from "./useAsyncCallback";

type Scope = { ready: boolean; orgId: string | null };

function matchingBilling(billing: BillingStatus, orgId: string) {
  if (billing.orgId !== orgId) throw new Error("Billing returned a different workspace. No payment action is available.");
  return billing;
}

export function useWorkspaceBilling(scope: Scope) {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorOrg, setErrorOrg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectingOrg, setRedirectingOrg] = useState<string | null>(null);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const mounted = useRef(true);
  const checkoutReturn = useRef<{ orgId: string; checkoutId: string; request: Promise<BillingStatus> } | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!scope.ready || !scope.orgId) return;
    let active = true;
    const orgId = scope.orgId;
    setBilling(null);
    setError(null);
    setErrorOrg(orgId);
    setRedirectingOrg(null);
    setLoading(true);
    if (getActiveOrgId() !== orgId) {
      setError("The selected workspace could not be verified. Reopen billing for this workspace before continuing.");
      setLoading(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const checkoutId = params.get("checkout") === "success" ? params.get("session_id") : null;
    if (checkoutId && !checkoutReturn.current) {
      checkoutReturn.current = { orgId, checkoutId, request: confirmSubscriptionCheckout({ checkoutId }).then((result) => result.billing) };
    }
    const confirmation = checkoutReturn.current;
    const reconciling = confirmation?.orgId === orgId && confirmation.checkoutId === checkoutId;
    const request = reconciling ? confirmation.request : getBillingStatus();
    void request.then((result) => {
      if (active) setBilling(matchingBilling(result, orgId));
    }).catch((cause) => {
      if (active) setError(cause instanceof Error ? cause.message : "Unable to load billing.");
    }).finally(() => {
      if (reconciling && mounted.current && window.location.pathname === "/billing") {
        const url = new URL(window.location.href);
        if (url.searchParams.get("session_id") === checkoutId) {
          url.searchParams.delete("checkout"); url.searchParams.delete("session_id");
          window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
        }
      }
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [scope.ready, scope.orgId]);

  const isCurrent = (orgId: string) => mounted.current && currentScope.current.ready && currentScope.current.orgId === orgId;
  const paymentOrg = () => {
    const activeOrg = getActiveOrgId();
    if (!scope.ready || !scope.orgId || !isCurrent(scope.orgId) || billing?.orgId !== scope.orgId) return null;
    if (activeOrg !== scope.orgId) {
      setErrorOrg(scope.orgId);
      setError("The selected workspace could not be verified. Reopen billing for this workspace before continuing.");
      return null;
    }
    return scope.orgId;
  };
  const checkoutAction = useAsyncCallback(async () => {
    const orgId = paymentOrg();
    if (!orgId) return;
    setError(null);
    setErrorOrg(orgId);
    try {
      const result = await createSubscriptionCheckout({ returnPath: `/billing?org=${encodeURIComponent(orgId)}` });
      if (!isCurrent(orgId)) return;
      setBilling(matchingBilling(result.billing, orgId));
      if (!result.completed && result.redirectUrl) {
        setRedirectingOrg(orgId);
        window.location.assign(result.redirectUrl);
      }
    } catch (cause) {
      if (isCurrent(orgId)) {
        setRedirectingOrg(null);
        setError(cause instanceof Error ? cause.message : "Unable to start checkout.");
      }
    }
  });
  const portalAction = useAsyncCallback(async () => {
    const orgId = paymentOrg();
    if (!orgId) return;
    setError(null);
    setErrorOrg(orgId);
    try {
      const result = await createCustomerPortal({ returnPath: `/billing?org=${encodeURIComponent(orgId)}` });
      if (!isCurrent(orgId)) return;
      setRedirectingOrg(orgId);
      window.location.assign(result.redirectUrl);
    } catch (cause) {
      if (isCurrent(orgId)) {
        setRedirectingOrg(null);
        setError(cause instanceof Error ? cause.message : "Unable to open billing management.");
      }
    }
  });
  const visibleBilling = scope.ready && billing?.orgId === scope.orgId ? billing : null;
  const visibleError = scope.ready && errorOrg === scope.orgId ? error : null;
  return {
    billing: visibleBilling, error: visibleError, loading: loading || (scope.ready && !visibleBilling && !visibleError),
    checkout: checkoutAction.callback, portal: portalAction.callback,
    paying: scope.ready && (checkoutAction.pending || portalAction.pending || redirectingOrg === scope.orgId),
  };
}
